import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CreditCardOutlined,
  QrcodeOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  FileTextOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  dealerService,
  DealerSubscription as DealerSub,
  DealerPackage,
  SubscribeResponse,
} from '../api/services/dealerService';
import { creditsService } from '../api/services/creditsService';

const { Title, Text, Paragraph } = Typography;
const ORANGE = '#fe5a1d';

const DealerSubscription: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userData = JSON.parse(localStorage.getItem('userData') || 'null');
  const dealerId: string | undefined = userData?.dealerId;

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<DealerSub | null>(null);
  const [packages, setPackages] = useState<DealerPackage[]>([]);
  const [selectedPkg, setSelectedPkg] = useState<DealerPackage | null>(null);
  const [showPkgModal, setShowPkgModal] = useState(false);
  const [paying, setPaying] = useState(false);
  const [cashData, setCashData] = useState<{ paymentId: string; paymentCode: string; instructions: string } | null>(null);
  const [cashStatus, setCashStatus] = useState<'PENDING' | 'COMPLETED' | 'FAILED'>('PENDING');
  const [cardMode, setCardMode] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchSubscription = async () => {
    if (!dealerId) return;
    const sub = await dealerService.getActiveSubscription(dealerId);
    setSubscription(sub);
    if (sub?.dealerPackage) setSelectedPkg(sub.dealerPackage);
  };

  useEffect(() => {
    (async () => {
      if (!dealerId) { setLoading(false); return; }
      try {
        const [pkgs, sub] = await Promise.all([
          dealerService.getDealerPackages(),
          dealerService.getActiveSubscription(dealerId),
        ]);
        setPackages(pkgs);
        setSubscription(sub);
        if (sub?.dealerPackage) {
          setSelectedPkg(sub.dealerPackage);
        } else if (pkgs.length > 0) {
          try {
            const reg = await dealerService.getDealerData(dealerId);
            const matched = pkgs.find(p => p.id === (reg as any).dealerPackageId) ?? pkgs[0];
            setSelectedPkg(matched);
          } catch {
            setSelectedPkg(pkgs[0]);
          }
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [dealerId]);

  // Poll cash payment
  useEffect(() => {
    if (!cashData || cashStatus !== 'PENDING') return;
    pollingRef.current = setInterval(async () => {
      try {
        const result = await creditsService.checkPaymentStatus(cashData.paymentId);
        if (result.status === 'COMPLETED') {
          setCashStatus('COMPLETED');
          clearInterval(pollingRef.current!);
          await fetchSubscription();
        } else if (result.status === 'FAILED') {
          setCashStatus('FAILED');
          clearInterval(pollingRef.current!);
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(pollingRef.current!);
  }, [cashData, cashStatus]);

  const activateFreeLocally = () => {
    const stored = localStorage.getItem('userData');
    if (stored) {
      const parsed = JSON.parse(stored);
      parsed.hasActiveSubscription = true;
      parsed.canAddListings = true;
      localStorage.setItem('userData', JSON.stringify(parsed));
    }
  };

  const handleCardPayment = async () => {
    if (!dealerId) return;
    setPaying(true);
    try {
      const result: SubscribeResponse = await dealerService.subscribeDealerPackage(dealerId, 'CARD', selectedPkg?.id);
      if (result.free) {
        activateFreeLocally();
        message.success(t('dealer_subscription.free_package_activated'));
        navigate('/dashboard/ads');
        return;
      }
      if (result.paymentUrl) {
        setCardMode(true);
        window.open(result.paymentUrl, '_blank');
      } else if (result.upayUrl && result.formFields) {
        setCardMode(true);
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = result.upayUrl;
        form.target = '_blank';
        Object.entries(result.formFields).forEach(([k, v]) => {
          const input = document.createElement('input');
          input.type = 'hidden'; input.name = k; input.value = v;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('dealer_subscription.card_payment_failed'));
    } finally {
      setPaying(false);
    }
  };

  const handleCashPayment = async () => {
    if (!dealerId) return;
    setPaying(true);
    try {
      const result: SubscribeResponse = await dealerService.subscribeDealerPackage(dealerId, 'CASH', selectedPkg?.id);
      if (result.free) {
        activateFreeLocally();
        message.success(t('dealer_subscription.free_package_activated'));
        navigate('/dashboard/ads');
        return;
      }
      setCashData({
        paymentId: result.paymentId,
        paymentCode: result.paymentCode!,
        instructions: result.instructions ?? t('dealer_subscription.default_cash_instructions'),
      });
      setCashStatus('PENDING');
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('dealer_subscription.cash_payment_failed'));
    } finally {
      setPaying(false);
    }
  };

  const daysRemaining = (endDate: string | null | undefined) => {
    if (!endDate) return null;
    return Math.max(0, Math.ceil((new Date(endDate).getTime() - Date.now()) / 86400000));
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  const isActive = subscription?.status === 'ACTIVE';
  const days = daysRemaining(subscription?.endDate);
  const isNearExpiry = days !== null && days <= 7;

  return (
    <>
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '0 8px 40px' }}>

        {/* Success state */}
        {cashStatus === 'COMPLETED' && (
          <Card style={{ borderRadius: 16, textAlign: 'center', padding: '40px 0', marginBottom: 24 }}>
            <Space direction="vertical" size="large">
              <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
              <Title level={3} style={{ color: '#52c41a' }}>{t('dealer_subscription.subscription_active')}</Title>
              <Paragraph>{t('dealer_subscription.can_post_now')}</Paragraph>
              <Button type="primary" size="large" style={{ background: ORANGE, borderColor: ORANGE }} onClick={() => navigate('/dashboard/ads')}>
                {t('dealer_subscription.go_to_my_ads')}
              </Button>
            </Space>
          </Card>
        )}

        {/* Package info */}
        {selectedPkg && cashStatus !== 'COMPLETED' && (
          <Card style={{ borderRadius: 16, marginBottom: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
              <div>
                <Title level={4} style={{ marginBottom: 4 }}>{selectedPkg.name} {t('dealer_subscription.package_suffix')}</Title>
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: 32, fontWeight: 800, color: ORANGE }}>{Number(selectedPkg.price).toLocaleString()}</span>
                  <span style={{ fontSize: 13, color: '#999', marginLeft: 6 }}>{selectedPkg.currency} {t('dealer_subscription.per_month')}</span>
                </div>
              </div>
              {packages.length > 1 && (
                <Button
                  icon={<SwapOutlined />}
                  size="small"
                  style={{ borderColor: ORANGE, color: ORANGE, borderRadius: 20 }}
                  onClick={() => setShowPkgModal(true)}
                >
                  {t('dealer_subscription.change_package')}
                </Button>
              )}
            </div>
            {selectedPkg.description && <Text type="secondary" style={{ display: 'block', marginBottom: 10 }}>{selectedPkg.description}</Text>}
            {selectedPkg.features && selectedPkg.features.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}>
                {selectedPkg.features.map((f, i) => (
                  <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
                    <CheckCircleFilled style={{ color: '#52c41a', fontSize: 13 }} />{f}
                  </span>
                ))}
              </div>
            )}
            {selectedPkg.maxListings != null && (
              <Tag color="orange" style={{ borderRadius: 20 }}>{t('dealer_subscription.listings_max', { count: selectedPkg.maxListings })}</Tag>
            )}
          </Card>
        )}

        {/* Active subscription status */}
        {isActive && cashStatus !== 'COMPLETED' && (
          <Card style={{ borderRadius: 16, marginBottom: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Title level={5} style={{ margin: 0 }}>{t('dealer_subscription.subscription_status')}</Title>
              <Tag color="success" style={{ borderRadius: 20, fontWeight: 700 }}>{t('dealer_subscription.active')}</Tag>
            </div>
            {subscription?.endDate && (
              <Text type="secondary">
                {t('dealer_subscription.valid_until', { date: new Date(subscription.endDate).toLocaleDateString('en-GB') })}
              </Text>
            )}
            {days !== null && (
              <div style={{ marginTop: 4 }}>
                <Text style={{ color: isNearExpiry ? '#f5222d' : '#555', fontWeight: isNearExpiry ? 700 : 400 }}>
                  {t('dealer_subscription.days_remaining', { count: days })}
                </Text>
              </div>
            )}
            {isNearExpiry && (
              <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 13 }}>
                {t('dealer_subscription.expiring_soon')}
              </Text>
            )}
          </Card>
        )}

        {/* Cash QR view */}
        {cashData && cashStatus === 'PENDING' && (
          <Card style={{ borderRadius: 16, textAlign: 'center', marginBottom: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <Space direction="vertical" size="large">
              <QrcodeOutlined style={{ fontSize: 48, color: ORANGE }} />
              <Title level={4}>{t('dealer_subscription.cash_payment')}</Title>
              <Paragraph type="secondary">{cashData.instructions}</Paragraph>
              <div style={{ background: '#f5f5f5', padding: '16px 32px', borderRadius: 12, display: 'inline-block' }}>
                <QRCodeSVG value={cashData.paymentCode} size={160} />
                <div style={{ marginTop: 12, fontSize: 22, fontWeight: 700, letterSpacing: 3 }}>{cashData.paymentCode}</div>
              </div>
              <Text type="secondary">{t('dealer_subscription.waiting_payment_confirmation')}</Text>
              <Button onClick={() => setCashData(null)}>{t('dealer_subscription.cancel')}</Button>
            </Space>
          </Card>
        )}

        {/* Card "open new tab" confirmation */}
        {cardMode && !cashData && cashStatus !== 'COMPLETED' && (
          <Card style={{ borderRadius: 16, textAlign: 'center', marginBottom: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <Space direction="vertical" size="large" style={{ padding: '20px 0' }}>
              <CreditCardOutlined style={{ fontSize: 48, color: ORANGE }} />
              <Title level={4}>{t('dealer_subscription.complete_in_new_tab')}</Title>
              <Paragraph type="secondary">{t('dealer_subscription.complete_in_new_tab_desc')}</Paragraph>
              <Button onClick={handleCardPayment} loading={paying} style={{ borderColor: ORANGE, color: ORANGE }}>{t('dealer_subscription.reopen_payment_page')}</Button>
              <Button onClick={() => setCardMode(false)}>{t('dealer_subscription.back')}</Button>
            </Space>
          </Card>
        )}

        {/* Payment options */}
        {(!isActive || isNearExpiry) && !cashData && !cardMode && cashStatus !== 'COMPLETED' && (
          <Card style={{ borderRadius: 16, marginBottom: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <Title level={4} style={{ marginBottom: 4 }}>{isActive ? t('dealer_subscription.renew_subscription') : t('dealer_subscription.subscribe')}</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>{t('dealer_subscription.choose_payment_method')}</Text>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Button
                  icon={<CreditCardOutlined />}
                  type="primary"
                  size="large"
                  block
                  loading={paying}
                  style={{ background: ORANGE, borderColor: ORANGE, height: 52 }}
                  onClick={handleCardPayment}
                >
                  {t('dealer_subscription.pay_with_card')}
                </Button>
              </Col>
              <Col xs={24} sm={12} style={{ marginTop: 0 }}>
                <Button
                  icon={<QrcodeOutlined />}
                  size="large"
                  block
                  loading={paying}
                  style={{ borderColor: ORANGE, color: ORANGE, height: 52 }}
                  onClick={handleCashPayment}
                >
                  {t('dealer_subscription.pay_with_cash')}
                </Button>
              </Col>
            </Row>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', textAlign: 'center', marginTop: 12 }}>
              {t('dealer_subscription.secure_payment_note')}
            </Text>
          </Card>
        )}

        {/* Invoices link */}
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <Button
            type="link"
            icon={<FileTextOutlined />}
            style={{ color: ORANGE }}
            onClick={() => navigate('/dashboard/invoices')}
          >
            {t('dealer_subscription.view_invoices')}
          </Button>
        </div>
      </div>

      {/* Package selector modal */}
      <Modal
        title={t('dealer_subscription.choose_package')}
        open={showPkgModal}
        onCancel={() => setShowPkgModal(false)}
        footer={null}
        width={520}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          {packages.map(p => {
            const isCurrent = p.id === selectedPkg?.id;
            return (
              <div
                key={p.id}
                onClick={() => { setSelectedPkg(p); setShowPkgModal(false); }}
                style={{
                  border: `2px solid ${isCurrent ? ORANGE : '#e0e0e0'}`,
                  borderRadius: 12,
                  padding: '14px 16px',
                  cursor: 'pointer',
                  background: isCurrent ? '#fff8f5' : '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'border-color 0.2s',
                }}
              >
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: isCurrent ? ORANGE : '#333' }}>{p.name}</div>
                  <div style={{ fontSize: 14, color: ORANGE, fontWeight: 600, marginTop: 2 }}>
                    {Number(p.price).toLocaleString()} {p.currency} {t('dealer_subscription.per_month')}
                  </div>
                  {p.description && <div style={{ fontSize: 12, color: '#888', marginTop: 3 }}>{p.description}</div>}
                  {p.maxListings != null && (
                    <Tag color="orange" style={{ borderRadius: 20, marginTop: 6, fontSize: 11 }}>{t('dealer_subscription.listings_max', { count: p.maxListings })}</Tag>
                  )}
                </div>
                {isCurrent && <CheckCircleFilled style={{ fontSize: 22, color: ORANGE }} />}
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
};

export default DealerSubscription;
